import React from 'react';
import c from 'classnames';
import { Highlight } from 'react-instantsearch';

import { SearchResultsInfinite } from '@uncinc/react-searchkit';
import type { Results } from '@uncinc/react-searchkit/types';

import { Image } from '@uncinc/uncinc-react-kitchen-sink';

import { constructVariableRatiosImageSourcesObject } from '../../../helpers/constructImageSources';
import ButtonWrapper from '../../ButtonWrapper';
import type { SearchAllContentHit } from '../types';
import PageHit, { type SearchAllContentPageHitProps } from './PageHit';

import './style.scss';

function getRenderItemProps(hit: SearchAllContentHit): SearchAllContentPageHitProps {
  const image = constructVariableRatiosImageSourcesObject(
    hit,
    ['1_1', '1_1', '4_3', '4_3', '4_3'],
    ['xxs', 'xxs', 'xxs', 'xxs', 'xxs'],
  );

  if ('vid' in hit) {
    return {
      contentType: hit.vid[0],
      link: hit.url[0],
      title: <Highlight attribute="taxonomy_name" hit={hit} />,
      subtitle: <Highlight attribute="taxonomy_description" hit={hit} />,
      image: image && (
        <Image
          src={image}
          alt={hit.taxonomy_image_alt?.[0]}
          lazyload={false}
        />
      ),
    };
  }

  if ('profile_type' in hit) {
    return {
      contentType: hit.profile_type[0],
      link: hit.url[0],
      title: (
        <>
          <Highlight attribute="first_name" hit={hit} />&nbsp;
          {hit.last_name?.[0] && (
            <Highlight attribute="last_name" hit={hit} />
          )}
        </>
      ),
      subtitle: <Highlight attribute="profile_description" hit={hit} />,
      image: image && (
        <Image
          src={image}
          alt={hit.profile_image_alt?.[0]}
          lazyload={false}
        />
      ),
    };
  }

  return {
    contentType: hit.content_type[0],
    link: hit.url[0],
    title: <Highlight attribute="page_title" hit={hit} />,
    // eslint-disable-next-line no-nested-ternary
    subtitle: <Highlight attribute={hit.body_summarized?.[0] ? 'body_summarized' : hit.body?.[0] ? 'body' : 'paragraphs_body'} hit={hit} />,
    hideTag:
      hit.content_type[0] === 'basic_page' ||
      hit.content_type[0] === 'landing_page' ||
      hit.content_type[0] === 'overview_page' ||
      hit.content_type[0] === 'homepage',
    image: image && (
      <Image
        src={image}
        alt={'image_alt' in hit ? hit.image_alt?.[0] : ''}
        lazyload={false}
      />
    ),
  };
}

const RenderItem = (hit: SearchAllContentHit) => {
  const renderItemProps = getRenderItemProps(hit);

  return (
    <PageHit {...renderItemProps} />
  );
};

const SearchAllContentResults: React.FC<Omit<Results, 'renderItem'>> = (props) => {
  const { className } = props;
  const classNames = c('SearchAllContent__Results', className);

  return (
    <SearchResultsInfinite
      className={classNames}
      renderItem={RenderItem}
      // eslint-disable-next-line react/no-unstable-nested-components
      showMoreButton={(buttonProps) => (
        <ButtonWrapper {...buttonProps} iconName="arrow_downward" />
      )}
    />
  );
};

export default SearchAllContentResults;
